import { generateClient } from 'aws-amplify/api';
import { logApiEvent } from 'src/analytics/KatalLogger';
import { getTimeDifference } from 'src/utils/date-time-utilities';
import moment from 'moment-timezone';

// Enhanced error information interface
interface EnhancedErrorInfo {
  message: string;
  statusCode?: number;
  errorCode?: string;
  requestId?: string;
  path?: string[];
  errorType?: string;
}

// Utility to format error messages for UI display
export const formatApiErrors = (errors: any[]): string => {
  if (errors.length === 0) return 'Unknown error occurred';

  const firstError = errors[0];
  // Prefer errorInfo.errorMessage for cleaner UI display
  return firstError.errorInfo?.errorMessage || firstError.message || 'Unknown error occurred';
};

// Utility to extract enhanced error information for logging
export const extractErrorInfo = (errors: any[]): EnhancedErrorInfo[] => {
  return errors.map((error) => ({
    message: error.message || 'Unknown error',
    statusCode: error.errorInfo?.statusCode,
    errorCode: error.errorInfo?.errorCode,
    requestId: error.errorInfo?.requestId,
    path: error.path,
    errorType: error.errorType
  }));
};

interface GraphQLRequestOptions {
  query: any;
  variables: any;
  requestName: string;
  requestType: 'query' | 'mutation';
}

export const fetchGraphQLRequest = async <T>({ query, variables, requestName, requestType }: GraphQLRequestOptions): Promise<T> => {
  const initiateTimer = moment();
  let response: any;
  let elapsed: number;

  try {
    const client = generateClient();
    response = await client.graphql({
      query: query,
      variables: variables
    });
    elapsed = getTimeDifference(initiateTimer);

    if (response.errors && response.errors.length > 0) {
      const errorMessage = formatApiErrors(response.errors);
      // Log all enhanced error information for debugging
      const enhancedErrorInfo = extractErrorInfo(response.errors);
      logApiEvent(
        {
          requestType: requestType,
          requestName: requestName,
          requestStatus: 'API_FAILED',
          responseTime: elapsed
        },
        { errors: response.errors, enhancedErrorInfo }
      );
      throw new Error(errorMessage);
    }

    logApiEvent({
      requestType: requestType,
      requestName: requestName,
      requestStatus: 'API_SUCCESS',
      responseTime: elapsed
    });

    return response.data;
  } catch (error: any) {
    elapsed = getTimeDifference(initiateTimer);
    const errorMessage = error.errors ? formatApiErrors(error.errors) : 'API Request Failed: Unexpected error occurred';
    logApiEvent(
      {
        requestType: requestType,
        requestName: requestName,
        requestStatus: 'API_FAILED',
        responseTime: elapsed
      },
      { errorMessage }
    );

    throw new Error(errorMessage);
  }
};
